/**
 * Transcript user state is a set of data that represents the current state of a user, who he is, what he is doing, etc.
 * It is meant to be shared between all other users in the same room for awareness purposes.
 */
export type TranscriptUserState = {
    selection: TranscriptUserStateSelection | null
}

export type TranscriptUserStateSelection = {
    paragraphId: string
    offset: number
}

export const TranscriptUserState = {
    EMPTY: {
        selection: null,
    } as TranscriptUserState,
}

export type TranscriptUserStateCallback = (localState: TranscriptUserState, remoteStates: TranscriptUserState[]) => void

export type TranscriptUserStateManager = {
    init: () => void

    getLocalState: () => TranscriptUserState
    getRemoteStates: () => TranscriptUserState[]

    updateSelection: (selection: TranscriptUserStateSelection | null) => void

    subscribe: (cb: TranscriptUserStateCallback) => () => void
    unsubscribe: (cb: TranscriptUserStateCallback) => void
}

export abstract class BaseTranscriptUserStateManager implements TranscriptUserStateManager {
    /**
     * Methods to be overridden by concrete implementations.
     */
    abstract init: () => void
    abstract getLocalState: () => TranscriptUserState
    abstract getRemoteStates: () => TranscriptUserState[]
    abstract updateSelection: (selection: TranscriptUserStateSelection | null) => void

    private _callbacks: TranscriptUserStateCallback[] = []

    public subscribe = (cb: TranscriptUserStateCallback) => {
        this._callbacks.push(cb)

        return () => this.unsubscribe(cb)
    }

    public unsubscribe = (cb: TranscriptUserStateCallback) => {
        this._callbacks = this._callbacks.filter((c) => c !== cb)
    }

    /**
     * Emits the local state to all subscribers.
     *
     * TODO: make sure we can differentiate between local state and remote states.
     */
    protected emit = () => {
        const localState = (this as unknown as TranscriptUserStateManager).getLocalState()
        const remoteStates = (this as unknown as TranscriptUserStateManager).getRemoteStates()

        for (const cb of this._callbacks) {
            cb(localState, remoteStates)
        }
    }
}

export class DummyTranscriptUserStateManager extends BaseTranscriptUserStateManager implements TranscriptUserStateManager {
    private _localState: TranscriptUserState = TranscriptUserState.EMPTY

    init = () => {}

    getLocalState = () => this._localState
    getRemoteStates = () => []

    updateSelection = (selection: TranscriptUserStateSelection | null) => {
        this.setLocalState({
            selection,
        })
    }

    private setLocalState = (state: TranscriptUserState) => {
        this._localState = state
        this.emit()
    }
}
